import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import SubHeader from '@/pages/profile/components/SubHeader';
import BottomSheet from '@/pages/profile/components/BottomSheet';
import { useToast } from '@/components/base/Toast';
import { supabase } from '@/lib/supabase';

interface VerifyData {
  name: string;
  idCard: string;
  phone: string;
  verified: boolean;
  verifiedAt: string | null;
  idCardFront: string | null;
  idCardBack: string | null;
}

const maskIdCard = (id: string) => {
  if (!id || id.length < 8) return id;
  return id.slice(0, 3) + '***********' + id.slice(-4);
};

const maskPhone = (phone: string) => {
  if (!phone || phone.length < 7) return phone;
  return phone.slice(0, 3) + '****' + phone.slice(-4);
};

const maskName = (name: string) => {
  if (!name) return '';
  if (name.length <= 1) return name;
  return name[0] + '*'.repeat(name.length - 1);
};

export default function VerifyPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [data, setData] = useState<VerifyData | null>(null);
  const [loading, setLoading] = useState(true);
  const [showInfo, setShowInfo] = useState(false);

  // 表单状态
  const [formName, setFormName] = useState('');
  const [formIdCard, setFormIdCard] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  // 身份证图片上传
  const [frontImage, setFrontImage] = useState<string | null>(null);
  const [backImage, setBackImage] = useState<string | null>(null);
  const [frontUploading, setFrontUploading] = useState(false);
  const [backUploading, setBackUploading] = useState(false);
  const frontRef = useRef<HTMLInputElement>(null);
  const backRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const userId = sessionData.session?.user?.id;
        if (!userId) {
          setLoading(false);
          return;
        }

        const { data: profileData, error } = await supabase
          .from('profiles')
          .select('name, id_card, phone, verified, created_at, id_card_front, id_card_back')
          .eq('id', userId)
          .maybeSingle();

        if (error) throw error;

        if (mounted && profileData) {
          setData({
            name: profileData.name || '',
            idCard: profileData.id_card || '',
            phone: profileData.phone || '',
            verified: profileData.verified ?? false,
            verifiedAt: profileData.created_at || null,
            idCardFront: profileData.id_card_front || null,
            idCardBack: profileData.id_card_back || null,
          });
          setFrontImage(profileData.id_card_front || null);
          setBackImage(profileData.id_card_back || null);
        }
      } catch {
        // 静默失败
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => { mounted = false; };
  }, []);

  const uploadImage = async (file: File, type: 'front' | 'back') => {
    if (type === 'front') setFrontUploading(true);
    else setBackUploading(true);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData.session?.user?.id;
      if (!userId) {
        showToast('登录已过期', 'error');
        return;
      }

      const ext = file.name.split('.').pop() || 'jpg';
      const path = `idcards/${userId}/${type}_${Date.now()}.${ext}`;
      const { error: uploadError } = await supabase.storage.from('public').upload(path, file, {
        cacheControl: '3600',
        upsert: true,
      });

      if (uploadError) {
        showToast('上传失败', 'error');
        return;
      }

      const { data: publicUrlData } = supabase.storage.from('public').getPublicUrl(path);
      const url = publicUrlData?.publicUrl;

      if (type === 'front') setFrontImage(url || null);
      else setBackImage(url || null);

      showToast('上传成功', 'success');
    } catch {
      showToast('上传失败', 'error');
    } finally {
      if (type === 'front') setFrontUploading(false);
      else setBackUploading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'front' | 'back') => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      showToast('请选择图片文件', 'error');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showToast('图片大小不能超过5MB', 'error');
      return;
    }
    uploadImage(file, type);
  };

  const validateForm = () => {
    if (!formName.trim()) return '请输入真实姓名';
    if (formName.trim().length < 2) return '姓名至少2个字符';
    if (!formIdCard.trim()) return '请输入身份证号';
    if (!/^\d{17}[\dXx]$/.test(formIdCard.trim())) return '请输入正确的18位身份证号';
    if (!frontImage) return '请上传身份证正面照片';
    if (!backImage) return '请上传身份证反面照片';
    return '';
  };

  const handleSubmit = async () => {
    if (submitting) return;
    const err = validateForm();
    if (err) {
      setFormError(err);
      return;
    }
    setSubmitting(true);
    setFormError('');

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData.session?.user?.id;
      if (!userId) {
        setFormError('登录已过期，请重新登录');
        setSubmitting(false);
        return;
      }

      const { error } = await supabase
        .from('profiles')
        .update({
          name: formName.trim(),
          id_card: formIdCard.trim(),
          verified: true,
          id_card_front: frontImage,
          id_card_back: backImage,
        })
        .eq('id', userId);

      if (error) throw error;

      showToast('实名认证成功', 'success');
      setData({
        name: formName.trim(),
        idCard: formIdCard.trim(),
        phone: data?.phone || '',
        verified: true,
        verifiedAt: new Date().toISOString(),
        idCardFront: frontImage,
        idCardBack: backImage,
      });
    } catch {
      setFormError('认证提交失败，请重试');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-brown-50 pb-20">
        <SubHeader title="实名认证" />
        <div className="flex items-center justify-center py-20">
          <span className="w-6 h-6 border-2 border-gold-500/30 border-t-gold-500 rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  // ===== 已认证状态 =====
  if (data?.verified) {
    const infoList = [
      { label: '真实姓名', value: maskName(data.name) },
      { label: '身份证号', value: maskIdCard(data.idCard) },
      { label: '手机号码', value: maskPhone(data.phone) },
    ];

    return (
      <div className="min-h-screen bg-brown-50 pb-20">
        <SubHeader title="实名认证" />

        {/* Verified Banner */}
        <div className="px-4 mt-4">
          <div className="bg-gradient-to-br from-gold-400 to-gold-600 rounded-2xl p-6 text-center relative overflow-hidden">
            <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-white/20"></div>
            <div className="absolute -bottom-8 -left-4 w-20 h-20 rounded-full bg-white/10"></div>
            <div className="relative">
              <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center mx-auto mb-3">
                <span className="w-9 h-9 flex items-center justify-center text-gold-600">
                  <i className="ri-shield-check-fill text-3xl"></i>
                </span>
              </div>
              <h2 className="text-brown-950 text-lg font-bold">已通过实名认证</h2>
              <p className="text-brown-800 text-xs mt-1">
                认证完成时间 {data.verifiedAt ? new Date(data.verifiedAt).toLocaleString('zh-CN', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                }).replace(/\//g, '-') : '—'}
              </p>
            </div>
          </div>
        </div>

        {/* Info Card */}
        <div className="px-4 mt-4">
          <div className="bg-white rounded-2xl p-5 shadow-sm">
            <h3 className="text-brown-900 text-sm font-bold mb-4">认证信息</h3>
            <div className="space-y-4">
              {infoList.map((item) => (
                <div key={item.label} className="flex items-center justify-between">
                  <span className="text-brown-500 text-sm">{item.label}</span>
                  <span className="text-brown-900 text-sm font-medium">{item.value || '—'}</span>
                </div>
              ))}
            </div>
            {/* 身份证图片 */}
            <div className="mt-4 pt-4 border-t border-brown-100">
              <h4 className="text-brown-600 text-sm mb-3">身份证照片</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-brown-50 rounded-xl overflow-hidden border border-brown-100">
                  <p className="text-xs text-brown-500 text-center py-1.5 bg-brown-100/50">身份证正面</p>
                  {data.idCardFront ? (
                    <img src={data.idCardFront} alt="身份证正面" className="w-full h-28 object-cover" />
                  ) : (
                    <div className="h-28 flex items-center justify-center text-brown-400">
                      <i className="ri-image-line text-2xl"></i>
                    </div>
                  )}
                </div>
                <div className="bg-brown-50 rounded-xl overflow-hidden border border-brown-100">
                  <p className="text-xs text-brown-500 text-center py-1.5 bg-brown-100/50">身份证反面</p>
                  {data.idCardBack ? (
                    <img src={data.idCardBack} alt="身份证反面" className="w-full h-28 object-cover" />
                  ) : (
                    <div className="h-28 flex items-center justify-center text-brown-400">
                      <i className="ri-image-line text-2xl"></i>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={() => setShowInfo(true)}
              className="mt-5 w-full py-3 rounded-xl bg-gold-50 text-gold-700 text-sm font-medium cursor-pointer whitespace-nowrap active:scale-[0.98] transition-transform"
            >
              查看认证说明
            </button>
          </div>
        </div>

        {/* Steps */}
        <div className="px-4 mt-4">
          <div className="bg-white rounded-2xl p-5 shadow-sm">
            <h3 className="text-brown-900 text-sm font-bold mb-5">认证权益</h3>
            <div className="space-y-5">
              {[
                { icon: 'ri-bank-card-line', title: '大额交易权限', desc: '解锁单笔 500g 以上金条预约' },
                { icon: 'ri-truck-line', title: '保价配送服务', desc: '实物黄金全额保价配送' },
                { icon: 'ri-exchange-funds-line', title: '快速提现', desc: '资产兑换资金快速到账' },
              ].map((item) => (
                <div key={item.title} className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gold-50 flex items-center justify-center flex-shrink-0">
                    <span className="w-5 h-5 flex items-center justify-center text-gold-600">
                      <i className={`${item.icon} text-lg`}></i>
                    </span>
                  </div>
                  <div className="flex-1">
                    <p className="text-brown-900 text-sm font-medium">{item.title}</p>
                    <p className="text-brown-400 text-xs mt-0.5">{item.desc}</p>
                  </div>
                  <span className="w-4 h-4 flex items-center justify-center text-gold-500">
                    <i className="ri-checkbox-circle-fill"></i>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <BottomSheet open={showInfo} onClose={() => setShowInfo(false)} title="认证说明">
          <div className="px-4 pb-8 text-sm text-brown-600 leading-relaxed space-y-3">
            <p>
              根据《中华人民共和国反洗钱法》及相关监管要求，贵金属交易平台须对用户进行实名认证，确保账户信息真实有效。
            </p>
            <p>
              您提交的身份信息仅用于实名认证与合规审查，我们将严格保护您的个人隐私，未经授权不会向任何第三方披露。
            </p>
            <p>
              如认证信息发生变更，请及时联系在线客服更新，以免影响您的交易与提现。
            </p>
          </div>
        </BottomSheet>
      </div>
    );
  }

  // ===== 未认证状态：显示表单 =====
  return (
    <div className="min-h-screen bg-brown-50 pb-20">
      <SubHeader title="实名认证" />

      {/* Unverified Banner */}
      <div className="px-4 mt-4">
        <div className="bg-gradient-to-br from-brown-100 to-brown-200 rounded-2xl p-6 text-center relative overflow-hidden">
          <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-white/20"></div>
          <div className="absolute -bottom-8 -left-4 w-20 h-20 rounded-full bg-white/10"></div>
          <div className="relative">
            <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center mx-auto mb-3">
              <span className="w-9 h-9 flex items-center justify-center text-brown-400">
                <i className="ri-shield-user-line text-3xl"></i>
              </span>
            </div>
            <h2 className="text-brown-900 text-lg font-bold">尚未实名认证</h2>
            <p className="text-brown-600 text-xs mt-1">完成认证后方可进行黄金交易</p>
          </div>
        </div>
      </div>

      {/* Form Card */}
      <div className="px-4 mt-4">
        <div className="bg-white rounded-2xl p-5 shadow-sm">
          <h3 className="text-brown-900 text-sm font-bold mb-4">提交认证信息</h3>

          <div className="space-y-4">
            {/* 姓名 */}
            <div>
              <label className="text-brown-600 text-sm block mb-1.5">真实姓名</label>
              <div className="flex items-center border border-brown-200 rounded-lg px-4 py-3 bg-white transition-all duration-200 hover:border-brown-300 focus-within:border-gold-400 focus-within:ring-2 focus-within:ring-gold-400/20">
                <input
                  type="text"
                  value={formName}
                  onChange={(e) => {
                    setFormName(e.target.value);
                    if (formError) setFormError('');
                  }}
                  placeholder="请输入真实姓名"
                  maxLength={20}
                  className="w-full text-sm text-brown-900 placeholder:text-brown-400 bg-transparent outline-none"
                />
              </div>
            </div>

            {/* 身份证号 */}
            <div>
              <label className="text-brown-600 text-sm block mb-1.5">身份证号</label>
              <div className="flex items-center border border-brown-200 rounded-lg px-4 py-3 bg-white transition-all duration-200 hover:border-brown-300 focus-within:border-gold-400 focus-within:ring-2 focus-within:ring-gold-400/20">
                <input
                  type="text"
                  inputMode="text"
                  value={formIdCard}
                  onChange={(e) => {
                    setFormIdCard(e.target.value.toUpperCase());
                    if (formError) setFormError('');
                  }}
                  placeholder="请输入18位身份证号"
                  maxLength={18}
                  className="w-full text-sm text-brown-900 placeholder:text-brown-400 bg-transparent outline-none"
                />
              </div>
            </div>

            {/* 手机号（只读） */}
            <div>
              <label className="text-brown-600 text-sm block mb-1.5">手机号码</label>
              <div className="flex items-center border border-brown-200/60 rounded-lg px-4 py-3 bg-brown-50/50">
                <input
                  type="text"
                  value={data?.phone || ''}
                  readOnly
                  className="w-full text-sm text-brown-500 bg-transparent outline-none"
                />
              </div>
              <p className="text-brown-400 text-[10px] mt-1">手机号码取自注册信息，不可修改</p>
            </div>

            {/* 身份证正面上传 */}
            <div>
              <label className="text-brown-600 text-sm block mb-1.5">身份证正面照片</label>
              <input
                ref={frontRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleFileChange(e, 'front')}
              />
              <button
                type="button"
                onClick={() => frontRef.current?.click()}
                disabled={frontUploading}
                className="w-full relative bg-brown-50 border border-brown-200 border-dashed rounded-xl overflow-hidden cursor-pointer active:scale-[0.98] transition-transform"
              >
                {frontImage ? (
                  <div className="relative">
                    <img src={frontImage} alt="身份证正面" className="w-full h-36 object-cover" />
                    <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                      <span className="text-white text-sm font-medium">点击重新上传</span>
                    </div>
                  </div>
                ) : (
                  <div className="h-36 flex flex-col items-center justify-center gap-2">
                    {frontUploading ? (
                      <span className="w-6 h-6 border-2 border-gold-500/30 border-t-gold-500 rounded-full animate-spin" />
                    ) : (
                      <>
                        <span className="w-10 h-10 flex items-center justify-center text-brown-400">
                          <i className="ri-upload-cloud-2-line text-2xl"></i>
                        </span>
                        <span className="text-brown-500 text-sm">点击上传身份证正面</span>
                        <span className="text-brown-400 text-xs">支持 JPG / PNG，不超过5MB</span>
                      </>
                    )}
                  </div>
                )}
              </button>
            </div>

            {/* 身份证反面上传 */}
            <div>
              <label className="text-brown-600 text-sm block mb-1.5">身份证反面照片</label>
              <input
                ref={backRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleFileChange(e, 'back')}
              />
              <button
                type="button"
                onClick={() => backRef.current?.click()}
                disabled={backUploading}
                className="w-full relative bg-brown-50 border border-brown-200 border-dashed rounded-xl overflow-hidden cursor-pointer active:scale-[0.98] transition-transform"
              >
                {backImage ? (
                  <div className="relative">
                    <img src={backImage} alt="身份证反面" className="w-full h-36 object-cover" />
                    <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                      <span className="text-white text-sm font-medium">点击重新上传</span>
                    </div>
                  </div>
                ) : (
                  <div className="h-36 flex flex-col items-center justify-center gap-2">
                    {backUploading ? (
                      <span className="w-6 h-6 border-2 border-gold-500/30 border-t-gold-500 rounded-full animate-spin" />
                    ) : (
                      <>
                        <span className="w-10 h-10 flex items-center justify-center text-brown-400">
                          <i className="ri-upload-cloud-2-line text-2xl"></i>
                        </span>
                        <span className="text-brown-500 text-sm">点击上传身份证反面</span>
                        <span className="text-brown-400 text-xs">支持 JPG / PNG，不超过5MB</span>
                      </>
                    )}
                  </div>
                )}
              </button>
            </div>
          </div>

          {/* 错误提示 */}
          {formError && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-100 rounded-lg px-3 py-2.5 mt-4">
              <span className="w-4 h-4 flex items-center justify-center text-red-500 flex-shrink-0">
                <i className="ri-error-warning-line text-sm"></i>
              </span>
              <span className="text-xs text-red-600">{formError}</span>
            </div>
          )}

          {/* 提交按钮 */}
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="mt-5 w-full py-3 rounded-xl bg-gold-500 hover:bg-gold-600 text-brown-950 text-sm font-semibold cursor-pointer whitespace-nowrap active:scale-[0.98] transition-all disabled:opacity-60"
          >
            {submitting ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-4 h-4 border-2 border-brown-950/30 border-t-brown-950 rounded-full animate-spin"></span>
                提交中...
              </span>
            ) : (
              '提交认证'
            )}
          </button>
        </div>
      </div>

      {/* Tips */}
      <div className="px-4 mt-4">
        <div className="bg-amber-50 rounded-xl p-4 border border-amber-100">
          <div className="flex items-center gap-2 mb-2">
            <i className="ri-information-line text-amber-600 text-lg"></i>
            <span className="text-amber-800 text-sm font-bold">认证须知</span>
          </div>
          <ul className="text-amber-700 text-xs leading-relaxed space-y-1.5 list-disc pl-4">
            <li>根据监管要求，交易前必须完成实名认证</li>
            <li>请确保姓名与身份证信息一致，照片清晰可辨</li>
            <li>身份信息仅用于合规审查，严格保密</li>
            <li>认证信息提交后不可自行修改，如需变更请联系客服</li>
          </ul>
        </div>
      </div>
    </div>
  );
}